<script lang="ts">
  import { onMount } from 'svelte';
  import { fetchMyHouseQuestionnaire, submitHouseQuestionnaire } from '../lib/convexApi';
  import type { HouseQuestionnaireAnswers, HouseQuestionnaireSubmission } from '../lib/types';

  type QuestionType =
    | 'text'
    | 'textarea'
    | 'email'
    | 'tel'
    | 'url'
    | 'number'
    | 'select'
    | 'multi-select'
    | 'slider'
    | 'boolean'
    | 'audience';

  interface QuestionOption {
    label: string;
    value: string;
  }

  interface Question {
    id: keyof HouseQuestionnaireAnswers;
    section: string;
    prompt: string;
    helper?: string;
    type: QuestionType;
    required?: boolean;
    options?: QuestionOption[];
    placeholder?: string;
  }

  const questions: Question[] = [
    {
      id: 'fullName',
      section: 'Section 1 — Basic Info',
      prompt: "What's your full name?",
      type: 'text',
      required: true,
      placeholder: 'First + last name'
    },
    {
      id: 'email',
      section: 'Section 1 — Basic Info',
      prompt: "What's your best email?",
      type: 'email',
      required: true,
      placeholder: 'you@example.com'
    },
    {
      id: 'phoneWhatsapp',
      section: 'Section 1 — Basic Info',
      prompt: 'Phone number for WhatsApp?',
      helper: 'Include country code, e.g. +351...',
      type: 'tel',
      required: true,
      placeholder: '+351...'
    },
    {
      id: 'instagram',
      section: 'Section 1 — Basic Info',
      prompt: 'Instagram handle (if you have one)?',
      type: 'text',
      required: false,
      placeholder: '@yourhandle'
    },
    {
      id: 'mainLink',
      section: 'Section 1 — Basic Info',
      prompt: 'Link to your website / portfolio / main online presence or photos/videos of your work',
      type: 'url',
      required: true,
      placeholder: 'https://...'
    },
    {
      id: 'whatYouDo',
      section: 'Section 2 — Creative + Business Snapshot',
      prompt: 'What do you do? (Describe your craft, business, or primary work in 1-3 sentences)',
      type: 'textarea',
      required: true
    },
    {
      id: 'useOfSpace',
      section: 'Section 3 — Space Needs',
      prompt: 'If you had a studio in Gravity House, what would you do with it? (EX: Host an event, host clients, create an art piece)',
      type: 'textarea',
      required: true
    },
    {
      id: 'squareMeters',
      section: 'Section 3 — Space Needs',
      prompt: 'How many square meters do you think you ideally need?',
      type: 'number',
      required: true,
      placeholder: 'e.g. 20'
    },
    {
      id: 'usageFrequency',
      section: 'Section 3 — Space Needs',
      prompt: 'How often would you, and/or other collaborators use the space?',
      type: 'select',
      required: true,
      options: [
        { label: 'Once per week', value: 'once_per_week' },
        { label: 'Several times per week', value: 'several_per_week' },
        { label: 'Daily', value: 'daily' },
        { label: 'Weekdays (5 days a week)', value: 'weekdays' },
        { label: 'All week (7 days, weekdays + weekends)', value: 'all_week' }
      ]
    },
    {
      id: 'timeWindows',
      section: 'Section 3 — Space Needs',
      prompt: 'What time windows do you typically operate in?',
      type: 'multi-select',
      required: true,
      options: [
        { label: 'Mornings', value: 'mornings' },
        { label: 'Afternoons', value: 'afternoons' },
        { label: 'Evenings', value: 'evenings' },
        { label: 'Late nights (12am–5am)', value: 'late_nights' }
      ]
    },
    {
      id: 'privacyPreference',
      section: 'Section 3 — Space Needs',
      prompt: 'Would you prefer a private room or are you open to sharing?',
      type: 'select',
      required: true,
      options: [
        { label: 'Only private', value: 'only_private' },
        { label: 'Prefer private but open to share', value: 'prefer_private_shared' },
        { label: 'Happy to share with selected people', value: 'happy_selected' },
        { label: 'Happy to share with anybody', value: 'happy_any' }
      ]
    },
    {
      id: 'sharedEnvironment',
      section: 'Section 3 — Space Needs',
      prompt: 'If sharing, what type of shared environment fits you best?',
      type: 'multi-select',
      required: false,
      helper: 'Choose all that fit. Required if you are open to sharing.',
      options: [
        { label: 'Workshop and Maker Space', value: 'maker_space' },
        { label: 'Creative studio', value: 'creative_studio' },
        { label: 'Wellness / healing room', value: 'wellness' },
        { label: 'Photography / content space', value: 'photo' },
        { label: 'Office / Co-working', value: 'coworking' },
        { label: 'Other', value: 'other' }
      ]
    },
    {
      id: 'usesSound',
      section: 'Section 4 — Sound + Activity Profile',
      prompt: 'Do you play music, use sound, or facilitate sessions that require volume?',
      type: 'boolean',
      required: true
    },
    {
      id: 'noiseTolerance',
      section: 'Section 4 — Sound + Activity Profile',
      prompt: "What’s your noise tolerance? (1 = needs deep quiet, 10 = can work next to a festival)",
      type: 'slider',
      required: true
    },
    {
      id: 'toolsUsed',
      section: 'Section 4 — Sound + Activity Profile',
      prompt:
        'What tools do you use for your work? (EX: Instruments, Paints, Canvases, Projectors, VR Headsets, Computer, DJ Decks, Cameras, Sound Capture Device, Space Age Crystals)',
      type: 'textarea',
      required: true
    },
    {
      id: 'hostsPeopleCount',
      section: 'Section 4 — Sound + Activity Profile',
      prompt: 'Will you bring clients, students, or groups into the space? If yes, how many people at a time?',
      type: 'number',
      required: true,
      placeholder: 'e.g. 10'
    },
    {
      id: 'okWithTransform',
      section: 'Section 5 — Community Fit',
      prompt:
        'If the space occasionally transforms for activations, events, or festivals (e.g. rooms repurposed for a weekend), are you comfortable with that?',
      type: 'select',
      required: true,
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'Yes, with notice', value: 'yes_notice' },
        { label: 'No, I want my own space', value: 'no' }
      ]
    },
    {
      id: 'contributeExperience',
      section: 'Section 5 — Community Fit',
      prompt: 'Would you be excited to contribute an experience when we host larger community events?',
      type: 'boolean',
      required: true
    },
    {
      id: 'multiFloorEvent',
      section: 'Section 5 — Community Fit',
      prompt: 'If you were to host a multi floor event in the middle of Barrio Alto, Lisbon, what would you do?',
      type: 'textarea',
      required: true
    },
    {
      id: 'idealSpace',
      section: 'Section 6 — Visioning',
      prompt: 'If you could design your ideal creative/work space, what would it look like?',
      type: 'textarea',
      required: true
    },
    {
      id: 'needsForBestWork',
      section: 'Section 6 — Visioning',
      prompt:
        'What do you need in order to do your best work? (e.g. light, materials, tech setup, quiet, storage, privacy, community around you…) ',
      type: 'textarea',
      required: true
    },
    {
      id: 'additionalSupport',
      section: 'Section 6 — Visioning',
      prompt: 'What additional resources or support would make this space truly transformative for you?',
      type: 'textarea',
      required: true
    },
    {
      id: 'desiredDuration',
      section: 'Section 7 — Logistics',
      prompt: 'How long would you ideally want access to the space?',
      type: 'select',
      required: true,
      options: [
        { label: '1 month', value: '1m' },
        { label: '3 months', value: '3m' },
        { label: '6 months', value: '6m' },
        { label: '12 months', value: '12m' },
        { label: 'Multi-year', value: 'multi-year' }
      ]
    },
    {
      id: 'startTiming',
      section: 'Section 7 — Logistics',
      prompt: 'How soon are you looking to begin?',
      type: 'text',
      required: true,
      placeholder: 'e.g. January, immediately, specific date...'
    },
    {
      id: 'anythingElse',
      section: 'Section 7 — Logistics',
      prompt: 'Anything else we should know about your needs, your work, or your vision',
      type: 'textarea',
      required: false
    },
    {
      id: 'audienceSizes',
      section: 'Section 7 — Logistics',
      prompt: 'How large is your current audience?',
      helper: 'Add Instagram, WhatsApp groups, email list, and any other platforms.',
      type: 'audience',
      required: true
    },
    {
      id: 'currentRevenue',
      section: 'Section 7 — Logistics',
      prompt: 'What is your approximate monthly revenue in your business right now?',
      type: 'select',
      required: true,
      options: [
        { label: '< €1k', value: '<1k' },
        { label: '€1–5k', value: '1-5k' },
        { label: '€5–10k', value: '5-10k' },
        { label: '€10–30k', value: '10-30k' },
        { label: '€30–80k', value: '30-80k' },
        { label: '€80k+', value: '80k+' }
      ]
    },
    {
      id: 'spaceBudget',
      section: 'Section 7 — Logistics',
      prompt: 'What would be a realistic budget if you were to rent a space per month?',
      type: 'select',
      required: true,
      options: [
        { label: 'Less than €100', value: '<100' },
        { label: '€100–200', value: '100-200' },
        { label: '€200–300', value: '200-300' },
        { label: '€400–500', value: '400-500' },
        { label: '€500–600', value: '500-600' },
        { label: '€600+', value: '600+' }
      ]
    },
    {
      id: 'referOthers',
      section: 'Section 7 — Logistics',
      prompt: 'Are there other people like yourself in Lisbon who are actively looking for a space? Add their contact info.',
      type: 'textarea',
      required: false
    },
    {
      id: 'spiritAnimal',
      section: 'Section 7 — Logistics',
      prompt: 'Final question — what is your spirit animal?',
      type: 'text',
      required: true,
      placeholder: 'e.g. wolf, dolphin, hummingbird...'
    }
  ];

  const defaultAnswers = (): HouseQuestionnaireAnswers => ({
    fullName: '',
    email: '',
    phoneWhatsapp: '',
    instagram: '',
    mainLink: '',
    whatYouDo: '',
    toolsUsed: '',
    currentRevenue: '',
    audienceSizes: {
      instagram: null,
      whatsappGroup: null,
      emailList: null,
      otherPlatforms: ''
    },
    useOfSpace: '',
    squareMeters: null,
    usageFrequency: '',
    timeWindows: [],
    privacyPreference: '',
    sharedEnvironment: [],
    sharedEnvironmentOther: '',
    usesSound: null,
    noiseTolerance: 5,
    hostsPeopleCount: null,
    okWithTransform: '',
    contributeExperience: null,
    multiFloorEvent: '',
    referOthers: '',
    idealSpace: '',
    needsForBestWork: '',
    additionalSupport: '',
    desiredDuration: '',
    startTiming: '',
    anythingElse: '',
    otherPeople: '',
    spaceBudget: '',
    spiritAnimal: ''
  });

  let answers: HouseQuestionnaireAnswers = defaultAnswers();
  let currentIndex = 0;
  let validationError = '';
  let loading = true;
  let submitting = false;
  let savingDraft = false;
  let submitError = '';
  let draftError = '';
  let submittedSubmission: HouseQuestionnaireSubmission | null = null;
  let showSuccess = false;
  let activeQuestion: Question = questions[0];

  onMount(async () => {
    try {
      const existing = await fetchMyHouseQuestionnaire();
      if (existing?.answers) {
        answers = { ...defaultAnswers(), ...existing.answers };
        submittedSubmission = existing;
        showSuccess = existing.finalized === true;
      }
    } catch (err) {
      console.error('Failed to load house questionnaire', err);
    } finally {
      loading = false;
    }
  });

  const totalQuestions = questions.length;
  $: activeQuestion = questions[currentIndex];
  let currentValue: any = null;
  $: currentValue = (answers as any)[activeQuestion.id];

  function toggleMulti(value: string, field: keyof HouseQuestionnaireAnswers) {
    const list = Array.isArray((answers as any)[field]) ? ([...(answers as any)[field]] as string[]) : [];
    const next = list.includes(value) ? list.filter(entry => entry !== value) : [...list, value];
    setAnswer(field, next);
  }

  function isSharing(): boolean {
    return (
      answers.privacyPreference === 'prefer_private_shared' ||
      answers.privacyPreference === 'happy_selected' ||
      answers.privacyPreference === 'happy_any'
    );
  }

  function requiredFor(question: Question): boolean {
    if (question.id === 'sharedEnvironment') {
      return isSharing();
    }
    return Boolean(question.required);
  }

  function getAnswerValue(id: keyof HouseQuestionnaireAnswers) {
    return (answers as any)[id];
  }

  function parseNumber(value: any): number | null {
    if (value === '' || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  function setAnswer(id: keyof HouseQuestionnaireAnswers, value: any) {
    answers = { ...answers, [id]: value } as HouseQuestionnaireAnswers;
  }

  function setAnswerFromEvent(id: keyof HouseQuestionnaireAnswers, event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    setAnswer(id, target?.value ?? '');
  }

  function setNumberFromEvent(id: keyof HouseQuestionnaireAnswers, event: Event) {
    const target = event.target as HTMLInputElement | null;
    setAnswer(id, parseNumber(target?.value ?? ''));
  }

  function handleAudienceInput(field: 'instagram' | 'emailList' | 'whatsappGroup', event: Event) {
    const target = event.target as HTMLInputElement | null;
    updateAudience(field, target?.value ?? '');
  }

  function handleAudienceOther(event: Event) {
    const target = event.target as HTMLInputElement | null;
    answers = {
      ...answers,
      audienceSizes: {
        ...answers.audienceSizes,
        otherPlatforms: target?.value ?? ''
      }
    };
  }

  function handleSliderInput(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const value = Number(target?.value ?? answers.noiseTolerance);
    setAnswer('noiseTolerance', value);
  }

  function resetFormState() {
    answers = defaultAnswers();
    currentIndex = 0;
    validationError = '';
    draftError = '';
  }

  async function persistDraft() {
    draftError = '';
    savingDraft = true;
    try {
      const result = await submitHouseQuestionnaire(answers, false);
      if (result) {
        submittedSubmission = result;
      }
    } catch (err) {
      console.error('Failed to save draft', err);
      draftError = err instanceof Error ? err.message : 'Could not save draft right now.';
    } finally {
      savingDraft = false;
    }
  }

  function validate(question: Question): string | null {
    const value = getAnswerValue(question.id);
    if (!requiredFor(question)) {
      return null;
    }

    if (question.type === 'multi-select') {
      const list = Array.isArray(value) ? value : [];
      if (question.id === 'sharedEnvironment' && !isSharing()) {
        return null;
      }
      if (!list.length) {
        return 'Please select at least one option.';
      }
      if (list.includes('other') && !(answers.sharedEnvironmentOther ?? '').trim()) {
        return 'Add details for “Other”.';
      }
      return null;
    }

    if (question.type === 'select') {
      return value ? null : 'Please choose an option.';
    }

    if (question.type === 'boolean') {
      return typeof value === 'boolean' ? null : 'Please choose yes or no.';
    }

    if (question.type === 'slider') {
      return typeof value === 'number' && value >= 1 && value <= 10 ? null : 'Choose a value from 1 to 10.';
    }

    if (question.type === 'number') {
      const num = parseNumber(value);
      if (num === null) {
        return 'Please enter a number.';
      }
      if (question.id === 'squareMeters' && num <= 0) {
        return 'Square meters should be greater than zero.';
      }
      if (question.id === 'hostsPeopleCount' && num <= 0) {
        return 'Please share an expected group size.';
      }
      return null;
    }

    if (question.type === 'audience') {
      const instagram = parseNumber(answers.audienceSizes.instagram);
      const whatsappGroup = parseNumber(answers.audienceSizes.whatsappGroup);
      const emailList = parseNumber(answers.audienceSizes.emailList);
      if (instagram === null && whatsappGroup === null && emailList === null) {
        return 'Add at least one audience size.';
      }
      return null;
    }

    const textValue = typeof value === 'string' ? value.trim() : '';
    if (!textValue) {
      return 'This field is required.';
    }

    if (question.type === 'email') {
      const valid = /\S+@\S+\.\S+/.test(textValue);
      return valid ? null : 'Enter a valid email.';
    }

    if (question.type === 'url') {
      try {
        new URL(textValue);
        return null;
      } catch (err) {
        try {
          new URL(`https://${textValue}`);
          return null;
        } catch (error) {
          return 'Enter a valid URL.';
        }
      }
    }

    return null;
  }

  function handlePrev() {
    validationError = '';
    if (currentIndex === 0) return;
    currentIndex -= 1;
  }

  async function handleNext() {
    const question = activeQuestion;
    const error = validate(question);
    if (error) {
      validationError = error;
      return;
    }
    validationError = '';
    await persistDraft();
    if (currentIndex < totalQuestions - 1) {
      currentIndex = Math.min(currentIndex + 1, totalQuestions - 1);
      return;
    }
    await handleSubmit();
  }

  function goToQuestion(idx: number) {
    if (idx < 0 || idx >= totalQuestions) return;
    currentIndex = idx;
    validationError = '';
  }

  async function handleSubmit() {
    submitting = true;
    submitError = '';
    try {
      const result = await submitHouseQuestionnaire(answers, true);
      submittedSubmission = result;
      showSuccess = true;
    } catch (err) {
      console.error('Failed to submit questionnaire', err);
      submitError =
        err instanceof Error ? err.message : 'Unable to submit right now. Please try again in a moment.';
    } finally {
      submitting = false;
    }
  }

  function updateAudience(field: 'instagram' | 'emailList' | 'whatsappGroup', value: string) {
    const parsed = parseNumber(value);
    answers = {
      ...answers,
      audienceSizes: {
        ...answers.audienceSizes,
        [field]: parsed
      }
    };
  }
</script>

<div class="house-shell">
  <header class="house-header">
    <div class="header-progress">
      <div class="progress-label">
        Question {Math.min(currentIndex + 1, totalQuestions)} of {totalQuestions}
      </div>
      <div class="progress-bar">
        <div class="progress-fill" style={`width: ${(Math.min(currentIndex + 1, totalQuestions) / totalQuestions) * 100}%`}></div>
      </div>
    </div>
  </header>

  <main class="house-main">
    {#if loading}
      <div class="card loading-card">
        <p class="helper">Loading your questionnaire...</p>
      </div>
    {:else if showSuccess}
      <div class="card success-card">
        <p class="section-label">Submission received</p>
        <h2>Thank you for applying to Gravity House Lisbon.</h2>
        <p class="helper">
          We’ll review your responses and follow up via WhatsApp or email if it’s a fit.
        </p>
        {#if submittedSubmission}
          <p class="meta-text">Last submitted: {new Date(submittedSubmission.submittedAt).toLocaleString()}</p>
        {/if}
      </div>
    {:else}
      {#if submitError}
        <div class="card error-card">
          <p class="error-message">{submitError}</p>
        </div>
      {/if}
      {#key activeQuestion.id}
        <article class="card question-card">
          <h2 class="question-text">
            {activeQuestion.prompt}
            {#if !requiredFor(activeQuestion)}
              <span class="optional-pill">Optional</span>
            {/if}
          </h2>

          {#if activeQuestion.type === 'textarea'}
            <textarea
              class="input textarea"
              rows="6"
              value={currentValue ?? ''}
              placeholder={activeQuestion.placeholder ?? ''}
              on:input={event => setAnswerFromEvent(activeQuestion.id, event)}
            ></textarea>
          {:else if activeQuestion.type === 'audience'}
            <div class="audience-grid">
              <label class="field">
                <span class="field-label">Instagram following</span>
                <input
                  class="input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={answers.audienceSizes.instagram ?? ''}
                  on:input={event => handleAudienceInput('instagram', event)}
                />
              </label>
              <label class="field">
                <span class="field-label">Owned WhatsApp group</span>
                <input
                  class="input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={answers.audienceSizes.whatsappGroup ?? ''}
                  on:input={event => handleAudienceInput('whatsappGroup', event)}
                />
              </label>
              <label class="field">
                <span class="field-label">Email list</span>
                <input
                  class="input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={answers.audienceSizes.emailList ?? ''}
                  on:input={event => handleAudienceInput('emailList', event)}
                />
              </label>
              <label class="field">
                <span class="field-label">Other platforms (Total)</span>
                <input
                  class="input"
                  type="text"
                  placeholder="TikTok, YouTube, LinkedIn..."
                  value={answers.audienceSizes.otherPlatforms ?? ''}
                  on:input={handleAudienceOther}
                />
              </label>
            </div>
          {:else if activeQuestion.type === 'multi-select'}
            <div class="option-grid">
              {#each activeQuestion.options ?? [] as option (option.value)}
                <button
                  type="button"
                  class:selected={(currentValue ?? []).includes(option.value)}
                  class="chip"
                  data-selected={(currentValue ?? []).includes(option.value)}
                  aria-pressed={(currentValue ?? []).includes(option.value)}
                  on:click={() => toggleMulti(option.value, activeQuestion.id)}
                >
                  {option.label}
                </button>
              {/each}
            </div>
            {#if activeQuestion.id === 'sharedEnvironment' && (currentValue ?? []).includes('other')}
              <input
                class="input"
                type="text"
                placeholder="Add details for other"
                value={answers.sharedEnvironmentOther ?? ''}
                on:input={event => setAnswerFromEvent('sharedEnvironmentOther', event)}
              />
            {/if}
          {:else if activeQuestion.type === 'select'}
            <div class="option-grid">
              {#each activeQuestion.options ?? [] as option (option.value)}
                <button
                  type="button"
                  class:selected={currentValue === option.value}
                  class="chip"
                  data-selected={currentValue === option.value}
                  aria-pressed={currentValue === option.value}
                  on:click={() => setAnswer(activeQuestion.id, option.value)}
                >
                  {option.label}
                </button>
              {/each}
            </div>
          {:else if activeQuestion.type === 'boolean'}
            <div class="option-grid">
              <button
                type="button"
                class:selected={currentValue === true}
                class="chip"
                data-selected={currentValue === true}
                aria-pressed={currentValue === true}
                on:click={() => setAnswer(activeQuestion.id, true)}
              >
                Yes
              </button>
              <button
                type="button"
                class:selected={currentValue === false}
                class="chip"
                data-selected={currentValue === false}
                aria-pressed={currentValue === false}
                on:click={() => setAnswer(activeQuestion.id, false)}
              >
                No
              </button>
            </div>
          {:else if activeQuestion.type === 'slider'}
            <div class="slider-row">
              <input
                type="range"
                min="1"
                max="10"
                step="1"
                value={answers.noiseTolerance}
                on:input={handleSliderInput}
              />
              <div class="slider-labels">
                <span>1</span>
                <span class="slider-value">{answers.noiseTolerance}</span>
                <span>10</span>
              </div>
            </div>
          {:else if activeQuestion.type === 'number'}
            <input
              class="input"
              type="number"
              min="0"
              value={currentValue ?? ''}
              on:input={event => setNumberFromEvent(activeQuestion.id, event)}
              placeholder={activeQuestion.placeholder ?? ''}
            />
          {:else}
            <input
              class="input"
              type={
                activeQuestion.type === 'email'
                  ? 'email'
                : activeQuestion.type === 'tel'
                ? 'tel'
                : activeQuestion.type === 'url'
                ? 'url'
                : 'text'
              }
              value={currentValue ?? ''}
              on:input={event => setAnswerFromEvent(activeQuestion.id, event)}
              placeholder={activeQuestion.placeholder ?? ''}
            />
          {/if}

          {#if validationError}
            <p class="error-message">{validationError}</p>
          {/if}
          {#if draftError}
            <p class="error-message">{draftError}</p>
          {/if}
          {#if activeQuestion.helper}
            <p class="helper">{activeQuestion.helper}</p>
          {/if}
        </article>
      {/key}

      <footer class="nav-bar">
        <button class="ghost" type="button" on:click={handlePrev} disabled={currentIndex === 0}>Back</button>
        <button type="button" on:click={handleNext} disabled={submitting}>
          {submitting ? 'Submitting...' : currentIndex === totalQuestions - 1 ? 'Submit' : 'Next →'}
        </button>
      </footer>
    {/if}
  </main>
</div>

<style>
  .house-shell {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5) var(--space-8);
  }

  .house-header {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: rgba(9, 10, 16, 0.88);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(244, 241, 232, 0.08);
    border-radius: var(--radius-lg);
  }

  .header-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
  }

  .progress-label {
    font-size: var(--font-size-sm);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-secondary);
  }

  .progress-bar {
    width: min(320px, 100%);
    height: 6px;
    background: rgba(244, 241, 232, 0.16);
    border-radius: 999px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--color-accent-amber-500);
    transition: width 220ms ease;
  }

  .house-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .card {
    width: min(820px, 100%);
    margin: 0 auto;
    background: rgba(17, 19, 28, 0.72);
    border: 1px solid rgba(244, 241, 232, 0.08);
    border-radius: 24px;
    box-shadow: 0 20px 48px rgba(8, 8, 12, 0.55);
    padding: clamp(1.5rem, 2vw, 2.75rem);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    backdrop-filter: blur(12px);
  }

  .question-card h2 {
    margin: 0;
  }

  .section-label {
    font-size: var(--font-size-xs);
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(244, 241, 232, 0.6);
  }

  .question-text {
    font-size: clamp(1.25rem, 2vw, 1.75rem);
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .optional-pill {
    font-size: var(--font-size-xs);
    letter-spacing: 0.1em;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    border: 1px solid rgba(244, 241, 232, 0.2);
    color: var(--color-text-secondary);
  }

  .input {
    width: 100%;
    padding: 0.95rem 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid rgba(244, 241, 232, 0.12);
    background: #171923;
    color: var(--color-text-primary);
    font-size: var(--font-size-base);
    box-shadow: inset 0 0 0 1px rgba(240, 169, 74, 0);
    transition: box-shadow var(--transition-fast), border var(--transition-fast);
  }

  .input:focus {
    outline: none;
    border: 1px solid rgba(240, 169, 74, 0.4);
    box-shadow: 0 0 0 3px rgba(240, 169, 74, 0.2);
  }

  .textarea {
    min-height: 180px;
    resize: vertical;
  }

  .option-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--space-2);
    width: 100%;
  }

  .chip {
    width: 100%;
    padding: 0.85rem 1rem;
    border-radius: var(--radius-lg);
    background: rgba(244, 241, 232, 0.06);
    border: 1px solid rgba(244, 241, 232, 0.12);
    color: var(--color-text-primary);
    text-align: left;
    transition: border var(--transition-fast), background var(--transition-fast), transform var(--transition-fast);
  }

  .chip:hover {
    background: rgba(244, 241, 232, 0.1);
    transform: translateY(-1px);
  }

  .chip.selected {
    border: 1px solid rgba(240, 169, 74, 0.8);
    background: rgba(240, 169, 74, 0.12);
  }

  .chip[data-selected='true'] {
    border: 1px solid rgba(240, 169, 74, 0.9);
    background: rgba(240, 169, 74, 0.18);
    color: var(--color-text-primary);
  }

  .slider-row {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .slider-row input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent-amber-500);
  }

  .slider-labels {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
  }

  .slider-value {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .helper {
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
    line-height: 1.5;
  }

  .error-message {
    color: var(--color-danger-500, #ff6b6b);
    font-size: var(--font-size-sm);
    margin: 0;
  }

  .nav-bar {
    position: sticky;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    width: min(820px, 100%);
    margin: 0 auto;
    padding: var(--space-3) 0;
    background: linear-gradient(180deg, rgba(17, 19, 28, 0) 0%, rgba(17, 19, 28, 0.95) 45%, rgba(17, 19, 28, 0.98) 100%);
  }

  .loading-card,
  .success-card,
  .error-card {
    width: min(720px, 100%);
  }

  .meta-text {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .audience-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--space-3);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .field-label {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(244, 241, 232, 0.6);
    font-size: var(--font-size-xs);
  }

  @media (max-width: 768px) {
    .house-shell {
      padding: var(--space-4) var(--space-4) 110px;
    }

    .house-header {
      justify-content: center;
    }

    .nav-bar {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      margin: 0;
      padding: var(--space-3) var(--space-4);
      background: rgba(17, 19, 28, 0.96);
      backdrop-filter: blur(14px);
      border-top: 1px solid rgba(244, 241, 232, 0.08);
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--space-3);
    }

    .nav-bar button {
      width: 100%;
    }
  }
</style>
